import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  name: string;
  icon: string;
  faqs: FaqItem[];
}

const faqCategories: FaqCategory[] = [
  {
    name: 'General Security',
    icon: 'ri-shield-check-line',
    faqs: [
      {
        question: 'Why do I need a professional security system?',
        answer:
          'A professional security system provides 24/7 protection for your property, deters criminals, and gives you real-time alerts when something happens. Studies show that homes without security systems are 300% more likely to be burglarized. Our systems also integrate with smart home technology, giving you complete control from your phone.',
      },
      {
        question: 'How long does a typical security installation take?',
        answer:
          'Most residential installations are completed within 2-4 hours. Commercial installations vary depending on the size and complexity of the project, typically ranging from 1-5 business days. Our technicians work efficiently to minimize disruption to your daily routine or business operations.',
      },
      {
        question: 'Do you offer free security assessments?',
        answer:
          'Yes! We provide complimentary on-site security assessments for both residential and commercial properties. Our certified security experts will evaluate your property, identify vulnerabilities, and recommend a customized solution tailored to your specific needs and budget.',
      },
      {
        question: 'Are your technicians licensed and insured?',
        answer:
          'Absolutely. All of our technicians are fully licensed, insured, and factory-certified by leading security manufacturers including Honeywell, DSC, Qolsys, and Hikvision. We maintain the highest industry standards and undergo continuous training on the latest security technologies.',
      },
      {
        question: 'What areas do you service?',
        answer:
          "We proudly serve the Greater Toronto Area and surrounding regions, including Mississauga, Brampton, Toronto, Hamilton, Oakville, Richmond Hill, Vaughan, and many more communities. If you're unsure whether we cover your area, please contact us and we'll be happy to help.",
      },
    ],
  },
  {
    name: 'Alarm Systems',
    icon: 'ri-alarm-warning-line',
    faqs: [
      {
        question: 'What happens when my alarm goes off?',
        answer:
          "When your alarm is triggered, our 24/7 monitoring center is immediately notified. Our trained operators will attempt to contact you to verify the alarm. If we cannot reach you or you confirm an emergency, we dispatch local authorities to your location within seconds. You'll also receive instant push notifications on your mobile app.",
      },
      {
        question: 'Can I control my alarm system from my phone?',
        answer:
          'Yes! All of our alarm packages include mobile app control. You can arm or disarm your system, receive real-time alerts, view activity history, and manage user codes—all from your smartphone or tablet, whether you\'re at home or halfway around the world.',
      },
      {
        question: 'Do I need a landline for the alarm system?',
        answer:
          'No landline required. All our alarm packages use cellular communication as the primary connection method. This ensures your system stays connected even if phone or internet lines are cut, providing a more reliable and secure connection than traditional landline systems.',
      },
      {
        question: "What if there's a power outage?",
        answer:
          'Our alarm systems include battery backup that keeps your system running for up to 24 hours during a power outage. Combined with cellular communication, your property remains fully protected even when the power is out. You\'ll receive a notification when the system switches to backup power.',
      },
      {
        question: 'Can I upgrade my alarm package later?',
        answer:
          'Absolutely! You can upgrade your package at any time. We\'ll credit your remaining balance toward the new package and our technicians will install any additional equipment needed at no extra charge. We make it easy to scale your security as your needs grow.',
      },
    ],
  },
  {
    name: 'Security Cameras',
    icon: 'ri-camera-line',
    faqs: [
      {
        question: 'What resolution cameras do you install?',
        answer:
          'We install cameras ranging from 2MP (1080p Full HD) to 8MP (4K Ultra HD). For most residential applications, we recommend 4MP or higher for crystal-clear footage. Our security experts will recommend the best resolution based on your specific needs, viewing distances, and coverage areas.',
      },
      {
        question: 'Can I view my cameras remotely?',
        answer:
          'Yes! All of our camera systems include remote viewing through our mobile app and web portal. You can watch live feeds, review recorded footage, receive motion alerts, and even communicate through two-way audio cameras—all from your smartphone, tablet, or computer.',
      },
      {
        question: 'How long is camera footage stored?',
        answer:
          'Storage duration depends on your package and recording settings. Our standard packages include 30 days of cloud storage. Local NVR (Network Video Recorder) systems can store 30-90 days of footage depending on the hard drive size and number of cameras. We can customize storage to meet your specific requirements.',
      },
      {
        question: 'Do cameras work at night?',
        answer:
          'All of our security cameras feature advanced infrared (IR) night vision technology, providing clear footage in complete darkness up to 100 feet. Our premium cameras also include ColorVu technology, which delivers full-color images even in low-light conditions for better identification.',
      },
      {
        question: 'Are outdoor cameras weatherproof?',
        answer:
          "Yes, all of our outdoor cameras are rated IP66 or IP67, meaning they're fully protected against dust, rain, snow, and extreme temperatures ranging from -30°C to 60°C. They're built to withstand Canadian winters and perform reliably year-round.",
      },
    ],
  },
  {
    name: 'Access Control',
    icon: 'ri-key-2-line',
    faqs: [
      {
        question: 'What types of access control do you offer?',
        answer:
          'We offer a full range of access control solutions including proximity card readers, key fob systems, biometric scanners (fingerprint and facial recognition), mobile credential systems using Bluetooth and NFC, and keypad entry systems. We\'ll recommend the best solution based on your security requirements and budget.',
      },
      {
        question: 'Can I manage access permissions remotely?',
        answer:
          'Yes! Our cloud-based access control systems allow you to add or remove users, change permissions, set time-based access schedules, view access logs, and receive alerts from anywhere using our secure web portal or mobile app. Changes take effect immediately.',
      },
      {
        question: 'What happens if an employee loses their access card?',
        answer:
          'Lost cards can be immediately deactivated through the management system, preventing unauthorized access. A new card can be programmed and issued within minutes, maintaining security without the expense of changing locks. This is one of the key advantages over traditional key systems.',
      },
      {
        question: 'Can access control integrate with my existing security?',
        answer:
          'Absolutely. Our access control systems integrate seamlessly with alarm systems, security cameras, and intercoms. When someone badges in, cameras can automatically record, and the alarm system can adjust accordingly. This creates a unified security ecosystem for maximum protection.',
      },
    ],
  },
  {
    name: 'Monitoring & Support',
    icon: 'ri-customer-service-2-line',
    faqs: [
      {
        question: 'What does 24/7 professional monitoring include?',
        answer:
          'Our 24/7 professional monitoring means trained security operators are watching over your property around the clock, 365 days a year. When an alarm is triggered, they verify the event, contact you, and dispatch emergency services if needed. This includes intrusion detection, fire monitoring, carbon monoxide detection, and medical emergency response.',
      },
      {
        question: 'How quickly do you respond to alarms?',
        answer:
          'Our average alarm response time is under 30 seconds. When an alarm is triggered, our monitoring center receives the signal instantly. An operator verifies the alarm and initiates the appropriate response protocol, which may include contacting you, dispatching police, fire, or medical services.',
      },
      {
        question: 'Is there a contract commitment?',
        answer:
          'We offer flexible options to suit your needs. Monthly plans have no long-term commitment—you can cancel anytime. Annual plans offer significant savings with a 12-month agreement. All plans include our 30-day money-back guarantee, so you can try our service risk-free.',
      },
      {
        question: 'What if I move to a new home or office?',
        answer:
          'We make moving easy! Our technicians will professionally relocate your system to your new property at no additional cost (within our service area). We\'ll also assess your new property and recommend any additional sensors or cameras needed for complete coverage.',
      },
      {
        question: 'Do you offer maintenance and service plans?',
        answer:
          'Yes, we offer comprehensive maintenance plans that include annual system inspections, battery replacements, software updates, and priority service calls. Regular maintenance ensures your system operates at peak performance and extends the life of your equipment.',
      },
    ],
  },
  {
    name: 'Pricing & Billing',
    icon: 'ri-money-dollar-circle-line',
    faqs: [
      {
        question: 'How much does a security system cost?',
        answer:
          'Security system costs vary based on your specific needs. Our residential alarm packages start at $29.99/month, camera packages start at $39.99/month, and we offer bundled solutions for comprehensive protection. We provide free quotes with no obligation, so you can make an informed decision.',
      },
      {
        question: 'Is professional installation included?',
        answer:
          'Professional installation is included with our Home Professional and Home Premium alarm packages, as well as all camera and access control packages. Our Home Basic package offers a DIY installation option with remote support from our technicians.',
      },
      {
        question: 'Do you offer financing options?',
        answer:
          'Yes, we offer flexible financing options to make security accessible for everyone. This includes 0% interest financing for qualified customers, monthly payment plans, and equipment lease options. Contact us to discuss the best payment arrangement for your budget.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept all major credit cards (Visa, Mastercard, American Express), debit cards, bank transfers, and pre-authorized monthly payments. For commercial clients, we also offer invoicing with net-30 payment terms.',
      },
    ],
  },
  {
    name: 'City-Specific Services',
    icon: 'ri-map-pin-line',
    faqs: [
      {
        question: 'Do you install security systems in Mississauga?',
        answer:
          'Yes, Mississauga is one of our primary service areas. We provide full security system installation, monitoring, and maintenance throughout Mississauga, including Streetsville, Port Credit, Erin Mills, Meadowvale, and Square One area. With Peel Regional Police reporting over 4,200 break-and-enters annually in the region, professional security is essential for Mississauga homeowners and businesses.',
      },
      {
        question: 'What security services do you offer in Brampton?',
        answer:
          'We offer comprehensive security services across all Brampton neighbourhoods including Bramalea, Heart Lake, Springdale, and Mount Pleasant. Our Brampton services include residential alarm systems, commercial security cameras, access control for warehouses and industrial units along the Highway 410 corridor, and 24/7 professional monitoring. We also provide same-day emergency service calls for existing Brampton customers.',
      },
      {
        question: 'Do you provide security camera installation in Toronto?',
        answer:
          'Absolutely. We install security cameras throughout Toronto, from downtown condos and retail stores to residential homes in Scarborough, North York, Etobicoke, and East York. Our Toronto installations include condo-friendly solutions that comply with building regulations, commercial-grade systems for businesses along Yonge Street and the Financial District, and residential packages for neighbourhoods across the city.',
      },
      {
        question: 'Can I get a security system installed in Hamilton?',
        answer:
          'Yes, we serve the entire Hamilton area including the Mountain, Dundas, Ancaster, Stoney Creek, and Waterdown. Hamilton has seen a rise in property crime in recent years, making professional security more important than ever. We offer free on-site assessments for Hamilton properties and can typically schedule installation within 3-5 business days of your consultation.',
      },
      {
        question: 'Do you service Oakville and Burlington for security installations?',
        answer:
          'We provide full security services throughout Oakville and Burlington, including Bronte, Glen Abbey, River Oaks, and Aldershot. Many of our Oakville clients choose our premium camera packages to protect high-value properties. We also serve nearby Milton and Halton Hills. Our Oakville team offers same-week installation for most residential packages.',
      },
      {
        question: 'Are your security services available in Richmond Hill and Vaughan?',
        answer:
          'Yes, Richmond Hill and Vaughan are core service areas. We install security systems throughout both cities, including Thornhill, Maple, Woodbridge, and Kleinburg. Many of our York Region clients opt for integrated systems combining alarms, cameras, and smart home automation. We also serve Markham, Newmarket, and Aurora with the same fast turnaround times.',
      },
      {
        question: 'Do you install security systems in Oshawa and Whitby?',
        answer:
          'We serve the entire Durham Region including Oshawa, Whitby, Ajax, Pickering, and Clarington. Our Durham Region team provides residential and commercial security installations with the same professional service and competitive pricing as our GTA locations. We offer free security assessments and can typically complete installations within one week of booking.',
      },
      {
        question: 'What is the best security company in the Greater Toronto Area?',
        answer:
          'Alliance Security Systems is rated among the top security providers in the GTA, with over 25 years of experience, 30,000+ installations, and a 98% customer satisfaction rate. Unlike national chains, we are locally owned and operated, meaning faster response times, personalized service, and deep knowledge of neighbourhood-specific security concerns across Mississauga, Toronto, Brampton, Hamilton, Oakville, and surrounding areas.',
      },
    ],
  },
  {
    name: 'System Comparisons',
    icon: 'ri-scales-3-line',
    faqs: [
      {
        question: 'Wired vs wireless security systems — which is better?',
        answer:
          'Both have advantages depending on your situation. Wired systems are more reliable for large commercial properties because they are not susceptible to wireless interference and never need battery replacements. Wireless systems are ideal for most homes and renters because they install faster, cause no wall damage, and are easier to expand. Our recommendation: wireless for residential (90% of our home installs are wireless) and wired or hybrid for commercial properties over 5,000 sq ft.',
      },
      {
        question: 'DIY security systems vs professional installation — what is the difference?',
        answer:
          'DIY systems like Ring and SimpliSafe are affordable upfront but have significant limitations: no professional monitoring guarantee, weaker cellular backup, limited camera quality, and you are responsible for troubleshooting. Professionally installed systems from Alliance Security include expert placement for optimal coverage, hardwired cellular backup, 24/7 ULC-listed monitoring, and ongoing maintenance. For serious home protection, professional installation reduces false alarms by up to 80% and ensures police dispatch priority.',
      },
      {
        question: 'Hikvision vs Dahua security cameras — which brand is better for homes?',
        answer:
          'Both are excellent manufacturers. Hikvision offers superior night vision with their ColorVu technology and has a wider range of AI-powered analytics like vehicle and person detection. Dahua provides strong value with competitive pricing and reliable performance. We primarily install Hikvision for residential clients due to their industry-leading image quality and 3-year warranty. For commercial clients, we recommend based on specific project requirements and budget.',
      },
      {
        question: 'NVR vs DVR — what is the difference for security camera recording?',
        answer:
          'DVR (Digital Video Recorder) works with analog coaxial cameras and processes video at the recorder. NVR (Network Video Recorder) works with IP cameras over ethernet or Wi-Fi and processes video at the camera before sending it to the recorder. NVR systems deliver significantly higher resolution (up to 4K), support longer cable runs, and offer more flexible installation. We recommend NVR systems for all new installations as they provide better image quality and future-proof your investment.',
      },
      {
        question: 'Monitored vs unmonitored alarm systems — is monitoring worth it?',
        answer:
          'Unmonitored systems rely entirely on you noticing alerts on your phone and calling police yourself. Monitored systems have trained operators who verify alarms and dispatch emergency services 24/7, even when you are asleep, at work, or travelling. Statistics show monitored homes are 3x less likely to be burglarized. Monitoring also qualifies you for insurance discounts of 15-20%, which often covers the monthly monitoring cost. We strongly recommend professional monitoring for complete protection.',
      },
      {
        question: 'Smart home security vs traditional security systems — what should I choose?',
        answer:
          'Traditional security systems focus purely on intrusion detection and monitoring. Smart home security integrates with devices like smart locks, lights, thermostats, and voice assistants for automated routines — for example, lights turning on when motion is detected at night. Our systems offer both: robust professional-grade security with smart home integration through platforms like Alarm.com. You get the reliability of a traditional system with the convenience of smart automation.',
      },
      {
        question: 'Indoor vs outdoor security cameras — do I need both?',
        answer:
          'For comprehensive protection, yes. Outdoor cameras deter criminals before they attempt entry and capture identifying footage like faces, vehicles, and license plates. Indoor cameras monitor activity inside your home, verify alarm events, and provide evidence if a break-in occurs. We recommend at minimum 2-4 outdoor cameras covering entry points and 1-2 indoor cameras in high-traffic areas. Our camera packages include both indoor and outdoor options.',
      },
    ],
  },
  {
    name: 'How-To & Troubleshooting',
    icon: 'ri-tools-line',
    faqs: [
      {
        question: 'How do I reset my alarm code?',
        answer:
          'To reset your alarm code: 1) Enter your current master code on the keypad, 2) Press the programming button (usually * or #), 3) Enter the new 4-6 digit code, 4) Press the confirm button. The exact steps vary by panel model — Honeywell, DSC, and Qolsys each have slightly different procedures. If you have forgotten your master code, contact our support team at 1-888-458-9181 and we can reset it remotely or schedule a technician visit.',
      },
      {
        question: 'How do I arm and disarm my security system?',
        answer:
          'You can arm your system in two modes: Stay mode (perimeter sensors active, interior motion sensors off — use when you are home) and Away mode (all sensors active — use when leaving). To arm, enter your code and press Stay or Away. To disarm, enter your code within the entry delay period (usually 30-45 seconds). You can also arm and disarm from the mobile app, which is the most convenient method for daily use.',
      },
      {
        question: 'Why is my alarm system beeping?',
        answer:
          'Common causes of alarm beeping: 1) Low battery — the panel or a sensor needs a battery replacement (most common), 2) Communication failure — the system lost its cellular or internet connection, 3) Sensor fault — a door or window sensor is malfunctioning or has been displaced, 4) AC power loss — the system is running on backup battery. Check your keypad display for a specific error message. For persistent beeping, contact our support team and we can diagnose the issue remotely.',
      },
      {
        question: 'How do I add a new user to my security system?',
        answer:
          'Through the Alarm.com app: Go to Settings, then Users, then Add User. Enter their name, assign a unique code, and set their permission level (full access, guest, or limited). Through the keypad: Enter your master code, access the user programming menu, select an available user slot, and program their code. You can also set time-based restrictions so certain users can only access the system during specific hours.',
      },
      {
        question: 'How do I view my security camera footage remotely?',
        answer:
          'Download our mobile app (available on iOS and Android), log in with your account credentials, and you will see live feeds from all your cameras. To review recorded footage, tap the timeline or playback button on any camera view. You can scrub through footage by date and time, save clips to your phone, and share them via email or text. Our web portal at the monitoring dashboard also provides full remote access from any computer.',
      },
      {
        question: 'My security camera image is blurry — how do I fix it?',
        answer:
          'Common fixes for blurry camera footage: 1) Clean the lens with a soft microfiber cloth — dust and spider webs are the most common cause, 2) Check the focus ring on the camera and adjust if needed, 3) Ensure the camera is not pointed directly at a bright light source causing glare, 4) Verify your NVR recording resolution is set to the maximum supported by the camera, 5) Check that the ethernet cable connection is secure. If the issue persists, contact us for a free diagnostic.',
      },
    ],
  },
  {
    name: 'Cost & Insurance Savings',
    icon: 'ri-money-dollar-circle-line',
    faqs: [
      {
        question: 'How much does a home security system cost in Ontario?',
        answer:
          'In Ontario, a professionally installed home security system typically costs between $0-$500 for equipment (depending on the package) plus $29.99-$59.99 per month for monitoring. Our Home Basic alarm package starts at $29.99/month with no upfront equipment cost. Camera systems range from $39.99-$79.99/month depending on the number of cameras. We offer free installation with most packages, which saves $200-$500 compared to competitors who charge separately for labour.',
      },
      {
        question: 'Do security systems lower home insurance premiums in Canada?',
        answer:
          'Yes, most Canadian insurance providers offer discounts of 5-20% on home insurance premiums for professionally monitored security systems. A ULC-listed monitoring certificate (which we provide) typically qualifies for the maximum discount. On an average Ontario home insurance policy of $1,500-$2,000 per year, this translates to $150-$400 in annual savings — often enough to cover or significantly offset your monthly monitoring cost.',
      },
      {
        question: 'What is the average cost of security camera installation in the GTA?',
        answer:
          'In the Greater Toronto Area, professional security camera installation averages $150-$300 per camera for labour, plus $100-$500 per camera for equipment depending on resolution and features. A typical 4-camera residential system costs $1,200-$2,500 total. At Alliance Security, our camera packages include professional installation at no extra charge, with monthly plans starting at $39.99 that include equipment, installation, cloud storage, and remote viewing.',
      },
      {
        question: 'Is a security system worth the monthly cost?',
        answer:
          'When you factor in the full picture, a security system typically pays for itself. The average Canadian burglary results in $5,000-$15,000 in losses. A monitored security system reduces break-in risk by up to 300%. Insurance discounts save $150-$400 per year. Beyond financial protection, you gain peace of mind, remote monitoring, fire and CO detection, and emergency dispatch. For $30-$60 per month — less than $2 per day — most homeowners consider it an essential investment.',
      },
      {
        question: 'Are there any hidden fees with security system contracts?',
        answer:
          'At Alliance Security, we believe in transparent pricing with no hidden fees. Your monthly rate covers monitoring, app access, cloud storage, and technical support. The only additional costs would be if you choose to add extra sensors or cameras beyond your package. We do not charge activation fees, service call fees for warranty issues, or cancellation penalties on our month-to-month plans. Always ask any security provider about these potential charges before signing.',
      },
      {
        question: 'How much does commercial security cost for a small business?',
        answer:
          'Small business security in the GTA typically ranges from $50-$150 per month for a basic alarm and monitoring package, and $100-$300 per month for a comprehensive system with cameras, access control, and monitoring. Factors that affect cost include square footage, number of entry points, camera count, and whether you need access control. We offer free commercial security assessments and customized quotes. Many business owners find that reduced theft and insurance savings make the system cost-neutral.',
      },
    ],
  },
];

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormSuccess, setShowFormSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredFaqs = searchQuery.trim()
    ? faqCategories.flatMap((cat) =>
        cat.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : [];

  const isSearching = searchQuery.trim().length > 0;

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) return;

    const data = new URLSearchParams();
    data.append('name', (formData.get('name') as string) ?? '');
    data.append('email', (formData.get('email') as string) ?? '');
    data.append('question_topic', (formData.get('question_topic') as string) ?? '');
    data.append('message', message ?? '');

    try {
      const response = await fetch('https://readdy.ai/api/form/d69sr4vmvg9ih2c7a6sg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        setShowFormSuccess(true);
        form.reset();
        setTimeout(() => setShowFormSuccess(false), 5000);
      } else {
        console.error('Form submission failed with status:', response.status);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  // Generate FAQ structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category => 
      category.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    )
  };

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://alliancesecurity.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://alliancesecurity.com/faq"
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Security Systems FAQ | Common Questions Answered | Alliance Security"
        description="Find answers to common questions about alarm systems, security cameras, access control, monitoring services, and pricing. Expert security advice from Alliance Security Systems."
        keywords="security systems FAQ, alarm system questions, security camera help, monitoring services, installation questions, security pricing"
        canonicalUrl="/faq"
        structuredData={[faqStructuredData, breadcrumbStructuredData]}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-6">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Home
              </Link>
              <Link
                to="/alarm-packages"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Alarm Systems
              </Link>
              <Link
                to="/camera-packages"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Cameras
              </Link>
              <Link
                to="/access-control"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Access Control
              </Link>
              <Link
                to="/clients"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Clients
              </Link>
              <Link
                to="/blog"
                className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
              >
                Blog
              </Link>
              <a
                href="tel:18884589181"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                1-888-458-9181
              </a>
              <a
                href="/#contact"
                className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                Get Free Quote
              </a>
            </div>
          </div>

          {/* Mobile dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
              <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
              <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarm Systems</Link>
              <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Cameras</Link>
              <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
              <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
              <Link to="/blog" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Blog</Link>
              <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">
                <i className="ri-phone-line"></i>1-888-458-9181
              </a>
              <a href="/#contact" className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2 block text-center">Get Free Quote</a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative py-16 sm:py-24 overflow-hidden" aria-labelledby="faq-hero-heading">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/58e4072363451829ca23db038b00713e.jpg"
              alt="FAQ Hero Background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/85 via-[#0A1628]/65 to-[#0A1628]/50"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-white/70 mb-6 sm:mb-8">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">FAQ</span>
            </nav>

            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block bg-[#D4A853] text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 whitespace-nowrap">HELP CENTER</span>
              <h1 id="faq-hero-heading" className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">Frequently Asked Questions</h1>
              <p className="text-base sm:text-xl text-white/80 mb-8 sm:mb-10 leading-relaxed px-2">
                Find answers to common questions about our security systems, monitoring services, installation process, and more.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-2xl mx-auto">
                <div className="w-10 sm:w-12 h-10 sm:h-12 absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 flex items-center justify-center">
                  <i className="ri-search-line text-lg sm:text-xl text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setOpenFaq(null);
                  }}
                  placeholder="Search for answers..."
                  className="w-full pl-12 sm:pl-14 pr-6 py-4 sm:py-5 rounded-2xl bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4A853] shadow-2xl"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="bg-[#0A1628] py-6 sm:py-8" aria-label="Company statistics">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {[
                { stat: '25+', label: 'Years Experience' },
                { stat: '30,000+', label: 'Installations' },
                { stat: '24/7', label: 'Expert Support' },
                { stat: '98%', label: 'Customer Satisfaction' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#D4A853] mb-1">{item.stat}</p>
                  <p className="text-xs sm:text-sm text-white/70">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <main>
          <section className="py-12 sm:py-24 bg-white" aria-labelledby="faq-content-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <h2 id="faq-content-heading" className="sr-only">FAQ Categories and Questions</h2>

              {isSearching ? (
                /* Search Results */
                <div className="max-w-3xl mx-auto">
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-2">
                      {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
                    </h2>
                    <p className="text-gray-500 text-sm">Showing matching questions from all categories</p>
                  </div>

                  {filteredFaqs.length > 0 ? (
                    <div className="space-y-4">
                      {filteredFaqs.map((faq, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden"
                        >
                          <button
                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                            className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer"
                          >
                            <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                            <i
                              className={`ri-arrow-down-s-line text-xl sm:text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${
                                openFaq === index ? 'rotate-180' : ''
                              }`}
                            ></i>
                          </button>
                          {openFaq === index && (
                            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 sm:py-16">
                      <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-search-line text-2xl sm:text-3xl text-gray-400"></i>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2">No results found</h3>
                      <p className="text-gray-500 mb-6 text-sm sm:text-base">Try different keywords or browse our categories below</p>
                      <button
                        onClick={() => setSearchQuery('')}
                        className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
                      >
                        Browse All Questions
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Category View */
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  {/* Category Sidebar - horizontal scroll on mobile */}
                  <div className="lg:w-72 flex-shrink-0">
                    <div className="lg:sticky lg:top-28">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Categories</h3>
                      <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
                        {faqCategories.map((category, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setActiveCategory(index);
                              setOpenFaq(0);
                            }}
                            className={`flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 rounded-xl text-left transition-all cursor-pointer flex-shrink-0 lg:flex-shrink lg:w-full ${
                              activeCategory === index
                                ? 'bg-[#D4A853]/10 text-[#0A1628] border border-[#D4A853]/30'
                                : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${activeCategory === index ? 'bg-[#D4A853] text-white' : 'bg-gray-100 text-gray-500'}`}>
                              <i
                                className={`${category.icon} text-base sm:text-lg ${activeCategory === index ? 'text-white' : 'text-gray-500'}`}
                              ></i>
                            </div>
                            <div className="min-w-0">
                              <span className={`text-xs sm:text-sm font-semibold block whitespace-nowrap lg:whitespace-normal ${activeCategory === index ? 'text-[#0A1628]' : ''}`}>{category.name}</span>
                              <span className="text-xs text-gray-400 hidden lg:block">{category.faqs.length} questions</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* FAQ List */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                      <div className="w-11 h-11 sm:w-14 sm:h-14 bg-[#D4A853] rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                        <i className={`${faqCategories[activeCategory].icon} text-xl sm:text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628]">{faqCategories[activeCategory].name}</h2>
                        <p className="text-gray-500 text-xs sm:text-sm">{faqCategories[activeCategory].faqs.length} frequently asked questions</p>
                      </div>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                      {faqCategories[activeCategory].faqs.map((faq, index) => (
                        <div
                          key={index}
                          className={`rounded-xl sm:rounded-2xl border overflow-hidden transition-all duration-300 ${
                            openFaq === index ? 'bg-[#D4A853]/5 border-[#D4A853]/30 shadow-md' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <button
                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                            className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer"
                          >
                            <div className="flex items-center gap-3 sm:gap-4 pr-3 sm:pr-4">
                              <span className={`text-xs sm:text-sm font-bold w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${openFaq === index ? 'bg-[#D4A853] text-white' : 'bg-gray-200 text-gray-500'}`}>{index + 1}</span>
                              <span className="font-semibold text-[#0A1628] text-sm sm:text-base">{faq.question}</span>
                            </div>
                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openFaq === index ? 'bg-[#D4A853] rotate-180' : 'bg-gray-200'}`}>
                              <i className={`ri-arrow-down-s-line text-base sm:text-lg ${openFaq === index ? 'text-white' : 'text-gray-500'}`}></i>
                            </div>
                          </button>
                          <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pl-14 sm:pl-[4.5rem]">
                              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Still Have Questions */}
          <section className="py-12 sm:py-24 bg-gray-50" aria-labelledby="contact-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                {/* Left - Contact Options */}
                <div>
                  <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 whitespace-nowrap">NEED MORE HELP?</span>
                  <h2 id="contact-heading" className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-4 sm:mb-6">Still Have Questions?</h2>
                  <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-10 leading-relaxed">
                    Can't find the answer you're looking for? Our security experts are here to help.
                  </p>

                  <div className="space-y-4 sm:space-y-6">
                    {[
                      { icon: 'ri-phone-line', title: 'Call Us', description: 'Speak directly with a security expert', action: '1-888-458-9181', href: 'tel:18884589181', tag: 'Available 24/7' },
                      { icon: 'ri-mail-line', title: 'Email Us', description: "Send us your question and we'll respond within 2 hours", action: 'info@securitysolutions.ca', href: 'mailto:info@securitysolutions.ca', tag: 'Response in 2hrs' },
                      { icon: 'ri-calendar-check-line', title: 'Book a Consultation', description: 'Schedule a free on-site security assessment', action: 'Schedule Now', href: '/#contact', tag: 'Free Assessment' },
                    ].map((item, index) => (
                      <a key={index} href={item.href} className="flex items-start gap-4 sm:gap-5 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:border-[#D4A853]/30 hover:shadow-lg transition-all duration-300 cursor-pointer group">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 bg-[#D4A853]/10 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853] transition-all">
                          <i className={`${item.icon} text-xl sm:text-2xl text-[#D4A853] group-hover:text-white transition-all`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
                            <h3 className="text-base sm:text-lg font-bold text-[#0A1628]">{item.title}</h3>
                            <span className="bg-[#D4A853]/10 text-[#D4A853] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold whitespace-nowrap">{item.tag}</span>
                          </div>
                          <p className="text-gray-500 text-xs sm:text-sm mb-1 sm:mb-2">{item.description}</p>
                          <span className="text-[#D4A853] font-semibold text-xs sm:text-sm flex items-center gap-1 group-hover:gap-2 transition-all truncate">{item.action} <i className="ri-arrow-right-line flex-shrink-0"></i></span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Right - Question Form */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-xl border border-gray-100">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-2">Ask a Question</h3>
                  <p className="text-gray-500 text-xs sm:text-sm mb-6 sm:mb-8">Submit your question and our team will get back to you promptly.</p>

                  {showFormSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 sm:px-6 py-3 sm:py-4 rounded-xl">
                      <p className="font-semibold text-sm sm:text-base">Question submitted successfully!</p>
                      <p className="text-xs sm:text-sm">We'll get back to you within 2 hours.</p>
                    </div>
                  )}

                  <form
                    data-readdy-form
                    id="faq-question-form"
                    onSubmit={handleFormSubmit}
                    className="space-y-4 sm:space-y-5"
                  >
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Your Name</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Question Topic</label>
                      <select
                        name="question_topic"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm cursor-pointer"
                      >
                        <option value="">Select a topic</option>
                        <option value="General Security">General Security</option>
                        <option value="Alarm Systems">Alarm Systems</option>
                        <option value="Security Cameras">Security Cameras</option>
                        <option value="Access Control">Access Control</option>
                        <option value="Monitoring & Support">Monitoring &amp; Support</option>
                        <option value="Pricing & Billing">Pricing &amp; Billing</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Your Question</label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Type your question here..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm resize-none"
                      ></textarea>
                      <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-[#D4A853] text-white py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
                    >
                      Submit Question
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 sm:py-20 bg-[#0A1628]" aria-labelledby="cta-heading">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <h2 id="cta-heading" className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Ready to Protect Your Property?</h2>
              <p className="text-base sm:text-xl text-white/70 mb-6 sm:mb-8">Get a free security assessment from our certified experts. No obligation, no pressure.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <a
                  href="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl inline-flex items-center justify-center gap-3"
                >
                  <i className="ri-calendar-line text-xl"></i>Schedule Free Consultation
                </a>
                <a
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-3"
                >
                  <i className="ri-phone-line text-xl"></i>1-888-458-9181
                </a>
              </div>
              <p className="text-white/50 text-xs sm:text-sm mt-4">No obligation • Response within 2 hours</p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <Link to="/" className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
              </Link>
              <p className="text-white/60 text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer"
              >
                Web Design
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
